import type { Auth, User } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import * as api from "./api";
import { dateLabel } from "./format";
import { go } from "./hooks";
import { REASON_TEXT } from "./parity";
import {
  ACCEPT_MAX,
  acceptFailure,
  acceptProblem,
  CHECK_HELP,
  CHECK_STATE,
  CHECK_TEXT,
  checkState,
  checkValueText,
  gateForm,
  gateFromForm,
  type GateForm,
  gateText,
  gateY,
  HAS_ROWS,
  missingText,
  percentText,
  shares,
  showsTrend,
  sparkLines,
  streakText,
  TREND_COLUMNS,
  trendRows,
} from "./parityHistory";
import { sortRows } from "./sort";
import { SortTh, useSort } from "./SortHeader";
import { codeText } from "./sheetImport";
import { type Failure, failureOf, Problem } from "./UserPages";
import { Skeleton } from "./Skeleton";

const SPARK_W = 120;
const SPARK_H = 24;

/** Nothing with fewer than 2 days: the caller shows the value only. */
export function Spark({ series, gate, label, width = SPARK_W }: { series: api.ParitySeries; gate: number; label: string; width?: number }) {
  if (!showsTrend(series)) return null;
  const lines = sparkLines(shares(series), width, SPARK_H);
  const first = series.cycles[0];
  const last = series.latest;
  return (
    <svg
      className="parity-spark"
      width={width}
      height={SPARK_H}
      viewBox={`0 0 ${width} ${SPARK_H}`}
      role="img"
      aria-label={last ? `${label}: ${percentText(first?.share)} at the start, ${percentText(last.share)} on ${last.day}` : `${label}: no rows yet`}
    >
      <line x1={0} x2={width} y1={gateY(gate, SPARK_H)} y2={gateY(gate, SPARK_H)} className="parity-spark-gate" />
      {lines.map((points) =>
        points.includes(" ") ? (
          <polyline key={points} points={points} className="parity-spark-line" />
        ) : (
          <circle key={points} cx={points.split(",")[0]} cy={points.split(",")[1]} r={1.5} className="parity-spark-dot" />
        ),
      )}
    </svg>
  );
}

/** Design 26.11 F2: the parity of each area and of the organization over time, the gate, and the notes of a cycle. */
export function ParityTrend({ base, auth, user, onArea }: { base: string; auth: Auth; user: User; onArea: (area: string) => void }) {
  const [found, setFound] = useState<api.ParityHistory | Failure | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [sort, setSort] = useSort("trend", TREND_COLUMNS);
  const [cycle, setCycle] = useState("");
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then(api.parityHistory)
      .then((page) => live && setFound(page))
      .catch((e) => live && setFound(failureOf(e)));
    return () => {
      live = false;
    };
  }, [user, attempt]);
  // An API before roadmap 086 has no history: the section does not show.
  if (found === "hidden") return null;
  const page = typeof found === "object" ? found : null;
  const rows = page ? trendRows(page) : [];
  const [whole, ...areas] = rows;
  const shown = whole ? [whole, ...sortRows(areas, TREND_COLUMNS, sort)] : [];
  const met = page ? page.areas.filter((area) => area.meets).length : 0;
  return (
    <details className="parity-trend" open data-testid="parity-trend">
      <summary>
        <h2>Trend</h2>{" "}
        {page && (
          <span className="sub" data-testid="parity-trend-count">
            {page.areas.length ? `${met} of ${page.areas.length} areas meet the gate` : "No days yet"}
          </span>
        )}
      </summary>
      {found === undefined ? (
        <Skeleton shape="table" columns={5} />
      ) : typeof found === "string" ? (
        <Problem found={found} auth={auth} retry={() => setAttempt((n) => n + 1)} />
      ) : (
        page && (
          <>
            <p data-testid="parity-gate">
              {gateText(page.gate)}{" "}
              {page.can_edit && !editing && (
                <button type="button" className="quiet" data-testid="parity-gate-edit" onClick={() => setEditing(true)}>
                  Change the gate
                </button>
              )}
            </p>
            {editing && (
              <GateEditor
                user={user}
                gate={page.gate}
                defaults={page.defaults}
                onClose={() => setEditing(false)}
                onSaved={() => {
                  setEditing(false);
                  setAttempt((n) => n + 1);
                }}
              />
            )}
            {!page.days.length ? (
              <p data-testid="parity-trend-empty">No parity days yet. Each night adds the parity of each area of that day.</p>
            ) : (
              <div className="table-scroll" tabIndex={0} role="region" aria-label="Parity trend by area">
                <table className="list parity-trend-table" data-testid="parity-trend-rows">
                  <thead>
                    <tr>
                      {TREND_COLUMNS.map((column) => (
                        <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((row) => (
                      <tr key={row.key || "org"} data-testid="parity-trend-row" data-area={row.key} data-gate={row.series.meets ? "met" : "not-met"}>
                        <th scope="row">
                          {row.key ? (
                            <button type="button" className="link" onClick={() => onArea(row.key)} aria-label={`Show the rows of ${row.label}`}>
                              {row.label}
                            </button>
                          ) : (
                            <strong>{row.label}</strong>
                          )}
                        </th>
                        <td data-label="Trend">{showsTrend(row.series) ? <Spark series={row.series} gate={page.gate.share} label={row.label} /> : <span className="sub">Not 2 days yet</span>}</td>
                        <td data-label="Last night" className="num">
                          {percentText(row.series.latest?.share)}
                          {row.series.latest && (
                            <span className="sub">
                              {" "}
                              of {row.series.latest.total.toLocaleString("en-US")} on {dateLabel(row.series.latest.day)}
                            </span>
                          )}
                        </td>
                        <td data-label="No explanation" className={row.series.latest?.unexplained ? "num error" : "num"}>
                          {row.series.latest ? row.series.latest.unexplained.toLocaleString("en-US") : "–"}
                        </td>
                        <td data-label="Cycles met" className="num">
                          {streakText(row.series.streak, page.gate.cycles)}
                        </td>
                        <td data-label="Gate" className="num">
                          {row.series.meets ? "Met" : "Not yet"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="footnote">
              The line is the share of the rows that agree each day, from 50% at the bottom to 100% at the top. The flat line is the gate. A cycle counts
              when its last day meets the gate. Only the cycles before this cycle count.
            </p>
            <CyclePick base={base} auth={auth} user={user} page={page} cycle={cycle} onCycle={setCycle} />
          </>
        )
      )}
    </details>
  );
}

function GateEditor({
  user,
  gate,
  defaults,
  onClose,
  onSaved,
}: {
  user: User;
  gate: api.ParityGate;
  defaults: api.ParityGate;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<GateForm>(() => gateForm(gate));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const parsed = gateFromForm(form);
  const problem = typeof parsed === "string" ? parsed : null;
  const save = async () => {
    if (typeof parsed === "string") return;
    setBusy(true);
    setError(null);
    try {
      await api.saveParityGate(await user.getIdToken(), parsed);
      onSaved();
    } catch (e) {
      setError(e instanceof api.ApiError && e.detail ? e.detail : "Not saved. Try again.");
      setBusy(false);
    }
  };
  const field = (key: keyof GateForm, label: string, unit: string) => (
    <div className="bulk-field">
      <label htmlFor={`parity-gate-${key}`}>
        {label} <span className="sub">({unit})</span>
      </label>
      <input
        id={`parity-gate-${key}`}
        data-testid={`parity-gate-${key}`}
        inputMode="decimal"
        value={form[key]}
        onChange={(e) => setForm((before) => ({ ...before, [key]: e.target.value }))}
      />
    </div>
  );
  return (
    <section className="bulk-edit" aria-labelledby="parity-gate-title" data-testid="parity-gate-form">
      <h3 id="parity-gate-title">Change the gate</h3>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <p className="sub">
          The defaults are {gateText(defaults).replace(/^The gate: /, "")} A change is an audit event.
        </p>
        {field("cycles", "Cycles in a row", "1 to 12")}
        {field("share", "Rows that agree", "%")}
        {field("tolerance", "Tolerance of each number", "%")}
        {problem && (
          <p className="error" data-testid="parity-gate-problem">
            {problem}
          </p>
        )}
        {error && (
          <p role="alert" className="error" data-testid="parity-gate-error">
            {error}
          </p>
        )}
        <p className="actions">
          <button type="submit" className="primary" data-testid="parity-gate-save" disabled={busy || Boolean(problem)}>
            {busy ? "Saving…" : "Save the gate"}
          </button>
          <button type="button" className="quiet" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </p>
      </form>
    </section>
  );
}

/** A pick of an earlier cycle shows its notes, as they were at the end of that cycle. */
function CyclePick({
  base,
  auth,
  user,
  page,
  cycle,
  onCycle,
}: {
  base: string;
  auth: Auth;
  user: User;
  page: api.ParityHistory;
  cycle: string;
  onCycle: (cycle: string) => void;
}) {
  const [notes, setNotes] = useState<api.ParityCycleNote[] | Failure | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  const earlier = cycle && cycle !== page.cycle_start ? cycle : "";
  useEffect(() => {
    if (!earlier) return;
    let live = true;
    setNotes(undefined);
    user
      .getIdToken()
      .then((token) => api.parityCycleNotes(token, earlier))
      .then((found) => live && setNotes(found.notes))
      .catch((e) => live && setNotes(failureOf(e)));
    return () => {
      live = false;
    };
  }, [user, earlier, attempt]);
  const summary = page.organization.cycles.find((found) => found.cycle_start === earlier);
  return (
    <div className="parity-cycle" data-testid="parity-cycle">
      <div className="filters">
        <div>
          <label htmlFor="parity-cycle-pick">Notes of the cycle</label>
          <select id="parity-cycle-pick" data-testid="parity-cycle-pick" value={earlier || page.cycle_start} onChange={(e) => onCycle(e.target.value)}>
            {page.cycles.map((start) => (
              <option key={start} value={start}>
                {start === page.cycle_start ? `This cycle (from ${start})` : `From ${start}`}
              </option>
            ))}
          </select>
        </div>
      </div>
      {!earlier ? (
        <p className="sub">The table above shows the notes of this cycle. Pick an earlier cycle to see its notes.</p>
      ) : (
        <>
          <p data-testid="parity-cycle-summary">
            {summary
              ? `On ${summary.day}, the last day of the cycle: ${percentText(summary.share)} of ${summary.total.toLocaleString("en-US")} rows agree, ${summary.unexplained.toLocaleString("en-US")} with no explanation. ${summary.met ? "The cycle met the gate." : "The cycle did not meet the gate."}`
              : "No parity day for this cycle."}
          </p>
          {notes === undefined ? (
            <Skeleton shape="table" columns={4} rows={3} />
          ) : typeof notes === "string" ? (
            notes === "hidden" ? null : <Problem found={notes} auth={auth} retry={() => setAttempt((n) => n + 1)} />
          ) : !notes.length ? (
            <p data-testid="parity-cycle-empty">No notes in this cycle.</p>
          ) : (
            <div className="table-scroll" tabIndex={0} role="region" aria-label="Notes of the cycle">
              <table className="list" data-testid="parity-cycle-notes">
                <thead>
                  <tr>
                    <th scope="col">Account</th>
                    <th scope="col">Area</th>
                    <th scope="col">Reason</th>
                    <th scope="col">Note</th>
                    <th scope="col">By</th>
                    <th scope="col">When</th>
                  </tr>
                </thead>
                <tbody>
                  {notes.map((note) => (
                    <tr key={note.account_id} data-testid="parity-cycle-note">
                      <th scope="row">
                        <a href={`${base}/accounts/${note.account_id}`} onClick={go}>
                          {note.number}
                        </a>{" "}
                        <span className="sub">{note.name}</span>
                      </th>
                      <td data-label="Area">{note.area ?? "–"}</td>
                      <td data-label="Reason">{REASON_TEXT[note.reason] ?? note.reason}</td>
                      <td data-label="Note">{note.note || "–"}</td>
                      <td data-label="By">{note.by ?? "–"}</td>
                      <td data-label="When">{note.at.slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const PAGE_OF: Partial<Record<api.ReadinessKey, { path: string; text: string }>> = {
  import: { path: "/import", text: "Open Admin > Import" },
  truck: { path: "/fleet", text: "Open the fleet" },
  routes: { path: "/route-planner", text: "Open the route planner" },
};

export function StateChip({ check }: { check: Pick<api.ReadinessCheck, "passed" | "accepted"> }) {
  const state = CHECK_STATE[checkState(check)];
  return (
    <span className={`chip-flag ${state.chip}`} data-testid="parity-ready-state">
      <span aria-hidden="true">{state.icon}</span> {state.text}
    </span>
  );
}

export function CheckDetail({
  base,
  auth,
  user,
  area,
  check,
  cycles,
  canAccept,
  heading: Heading = "h3",
  onClose,
  onArea,
  onSaved,
}: {
  base: string;
  auth: Auth;
  user: User;
  area: api.ReadinessArea;
  check: api.ReadinessCheck;
  cycles: number;
  canAccept: boolean;
  /** The level under the heading of the page around it (axe heading-order). */
  heading?: "h2" | "h3";
  onClose: () => void;
  /** Without it, the parity check links to the Parity page. */
  onArea?: (area: string) => void;
  onSaved: () => void;
}) {
  const [rows, setRows] = useState<api.ReadinessRow[] | Failure | undefined>(undefined);
  const [limit, setLimit] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const listed = HAS_ROWS.includes(check.key) && !check.passed;
  useEffect(() => {
    if (!listed) return;
    let live = true;
    user
      .getIdToken()
      .then((token) => api.readinessRows(token, area.area, check.key))
      .then((found) => {
        if (!live) return;
        setRows(found.rows);
        setLimit(found.limit);
      })
      .catch((e) => live && setRows(failureOf(e)));
    return () => {
      live = false;
    };
  }, [user, area.area, check.key, listed, attempt]);
  const page = PAGE_OF[check.key];
  const title = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    const heading = title.current;
    if (!heading) return;
    const box = heading.getBoundingClientRect();
    if (box.top < 0 || box.bottom > window.innerHeight) heading.scrollIntoView({ block: "start" });
    heading.focus({ preventScroll: true });
  }, []);
  return (
    <section className="parity-ready-detail" aria-labelledby="parity-ready-title" data-testid="parity-ready-detail">
      <Heading id="parity-ready-title" tabIndex={-1} ref={title}>
        {area.area} · {CHECK_TEXT[check.key]}: {check.passed ? "passes" : check.accepted ? "fails, accepted" : "fails"}
      </Heading>
      <p className="sub">{CHECK_HELP[check.key]}</p>
      <p>{checkValueText(check, cycles)}</p>
      {check.accepted && (
        <p data-testid="parity-ready-accepted">
          Accepted by {check.accepted.by ?? "a former member"} on {dateLabel(check.accepted.at)}: {check.accepted.reason}
        </p>
      )}
      {check.key === "parity" && (
        <p>
          {onArea ? (
            <button type="button" className="link" onClick={() => onArea(area.area)}>
              Show the rows of {area.area} that do not agree
            </button>
          ) : (
            <a href={`${base}/parity`} onClick={go}>
              Open the Parity page
            </a>
          )}
        </p>
      )}
      {page && (
        <p>
          <a href={`${base}${page.path}`} onClick={go}>
            {page.text}
          </a>
        </p>
      )}
      {listed &&
        (rows === undefined ? (
          <Skeleton shape="table" columns={4} rows={3} />
        ) : typeof rows === "string" ? (
          rows === "hidden" ? null : <Problem found={rows} auth={auth} retry={() => setAttempt((n) => n + 1)} />
        ) : !rows.length ? (
          <p data-testid="parity-ready-no-rows">No row fails now.</p>
        ) : (
          <>
            <div className="table-scroll" tabIndex={0} role="region" aria-label="Rows that fail">
              <table className="list" data-testid="parity-ready-failing">
                <thead>
                  <tr>
                    <th scope="col">{check.key === "drivers" ? "Driver" : "Account"}</th>
                    <th scope="col">{check.key === "import" ? "Issue" : "Missing"}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={`${row.account_id ?? row.member_id ?? ""}-${row.code ?? ""}-${i}`} data-testid="parity-ready-failing-row">
                      <th scope="row">
                        {row.account_id ? (
                          <>
                            <a href={`${base}/accounts/${row.account_id}`} onClick={go}>
                              {row.number}
                            </a>
                            {row.name && <span className="sub"> {row.name}</span>}
                          </>
                        ) : row.member_id ? (
                          <a href={`${base}/users/${row.member_id}`} onClick={go}>
                            {row.name || "Unknown member"}
                          </a>
                        ) : (
                          (row.number ?? "–")
                        )}
                      </th>
                      <td data-label={check.key === "import" ? "Issue" : "Missing"}>
                        {row.code ? `${codeText(row.code)}${row.row ? ` (row ${row.row})` : ""}` : missingText(row) || "–"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {rows.length >= limit && <p className="footnote">The first {limit.toLocaleString("en-US")} rows only.</p>}
          </>
        ))}
      {canAccept && !check.passed && <AcceptForm user={user} area={area.area} check={check.key} again={Boolean(check.accepted)} onSaved={onSaved} />}
      <p className="actions">
        <button type="button" className="quiet" data-testid="parity-ready-close" onClick={onClose}>
          Close
        </button>
      </p>
    </section>
  );
}

function AcceptForm({ user, area, check, again, onSaved }: { user: User; area: string; check: api.ReadinessKey; again: boolean; onSaved: () => void }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const problem = acceptProblem(reason);
  const tooLong = reason.length > ACCEPT_MAX;
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.acceptReadiness(await user.getIdToken(), area, check, reason);
      console.info(`parity: accepted ${area} ${check}${again ? " again" : ""}`);
      setReason("");
      setBusy(false);
      onSaved();
    } catch (e) {
      console.warn(`parity: the accept of ${area} ${check} failed (${e instanceof api.ApiError ? e.status : "no answer"})`);
      setError(acceptFailure(e));
      setBusy(false);
    }
  };
  return (
    <form
      className="parity-ready-accept"
      data-testid="parity-ready-accept"
      onSubmit={(e) => {
        e.preventDefault();
        if (!problem) void save();
      }}
    >
      <div className="bulk-field">
        <label htmlFor="parity-ready-reason">{again ? "Accept again to change the reason" : "Accept the fail: the reason"}</label>
        <textarea
          id="parity-ready-reason"
          data-testid="parity-ready-reason"
          rows={2}
          value={reason}
          aria-invalid={tooLong || undefined}
          aria-describedby={tooLong ? "parity-ready-reason-error" : undefined}
          onChange={(e) => setReason(e.target.value)}
        />
        {tooLong && (
          <span id="parity-ready-reason-error" className="error" data-testid="parity-ready-reason-error">
            {problem}
          </span>
        )}
      </div>
      {error && (
        <p role="alert" className="error" data-testid="parity-ready-accept-error">
          {error}
        </p>
      )}
      <p className="actions">
        <button type="submit" className={again ? "quiet" : "primary"} data-testid="parity-ready-accept-save" disabled={busy || Boolean(problem)}>
          {busy ? "Saving…" : again ? "Accept again" : "Accept the fail"}
        </button>
      </p>
    </form>
  );
}
